import type { Observable } from 'rxjs';
import type { Uuid } from '@common/types';
import type { NormalizedTabEntity, DenormalizedTabEntity } from '@web/types';

import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { tabInstance } from './tabs.models';

import TabsActions from './tabs.actions';
import * as TabSelectors from './tabs.selectors';

@Injectable()
export class TabsFacade {
    private readonly store = inject(Store);

    public readonly tabsEntities$ = this.store.select(TabSelectors.selectTabsEntities);

    /**
     * Use the initialization action to perform one
     * or more tasks in your Effects.
     */
    public init(entities: NormalizedTabEntity[]): void {
        this.store.dispatch(TabsActions.init({ entities }));
    }

    public add(entity: DenormalizedTabEntity, order: number): void {
        this.store.dispatch(TabsActions.add({ entity, order }));
    }

    public create(parentId: Uuid, order: number, meta: { variant: number }): void {
        this.store.dispatch(TabsActions.create({ parentId, order, meta }));
    }

    public updateProperties(id: Uuid, changes: Partial<NormalizedTabEntity>): void {
        this.store.dispatch(TabsActions.updateProperties({ update: { id, changes } }));
    }

    public appendChild(entityId: string, childId: string, order: number = 0): void {
        this.store.dispatch(TabsActions.appendChild({ entityId: entityId, childId, order }));
    }

    public remove(entity: DenormalizedTabEntity, order?: number, skipHistory: boolean = false): void {
        this.store.dispatch(TabsActions.remove({ entity, order, skipHistory }));
    }

    public copy(entity: DenormalizedTabEntity, order?: number): void {
        this.store.dispatch(TabsActions.copy({ entity: entity, order }));
    }

    public getTabInstanceValue(parentId: Uuid): NormalizedTabEntity {
        return tabInstance({ parentId });
    }

    public denormalizedById$(id: Uuid): Observable<DenormalizedTabEntity> {
        return this.store.select(TabSelectors.selectTabEntityById(id));
    }

    public byId$(id: Uuid): Observable<NormalizedTabEntity> {
        return this.store.select(TabSelectors.selectTabById(id));
    }
}
